import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import AtelierModalCreeFicheMaster from "../atelier/AtelierModalCreeFicheMaster"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { useRights } from "../../hooks/rights"
import { DASHBOARD_LAYOUT, resolveLayoutItem } from "../../config/navConfig"

const styles = {
	// Hauteur/padding hérités de gs.page (déduction de la nav + border-box) :
	// pas de copie locale, sinon le fix de hauteur ne suit pas.
	page: {
		...gs.page,
		padding: "40px 24px",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	inner: {
		width: "100%",
		maxWidth: 880,
	},
	title: {
		fontSize: 26,
		fontWeight: 600,
		color: COLORS.text,
		letterSpacing: "-0.02em",
		margin: 0,
	},
	subtitle: {
		fontSize: 14,
		color: COLORS.textMuted,
		marginTop: 6,
		marginBottom: 28,
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))",
		gap: 16,
	},
	cardLabel: {
		fontSize: 14,
		fontWeight: 600,
		color: COLORS.text,
		textAlign: "center",
	},
}

const cardBase = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	gap: 16,
	padding: "30px 20px",
	minHeight: 150,
	backgroundColor: COLORS.surface,
	border: `1px solid ${COLORS.border}`,
	borderRadius: 16,
	textDecoration: "none",
	cursor: "pointer",
	boxSizing: "border-box",
	width: "100%",
	transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
}

// Stationary hit-area wrapper: the hover handlers live here so the detection
// zone never moves, while the card inside is free to translate (lift) without
// pulling itself out from under the cursor.
const cardWrap = {
	display: "flex",
	width: "100%",
}

const iconTile = (active) => ({
	width: 54,
	height: 54,
	borderRadius: 15,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontSize: 22,
	backgroundColor: active ? COLORS.accentLight : COLORS.bg,
	color: active ? COLORS.accent : COLORS.text,
	transition: "background-color 0.15s, color 0.15s",
})

const ActionCard = ({ icon, label, to, onClick, disabled }) => {
	const [hover, setHover] = useState(false)
	const lift = hover && !disabled
	const style = {
		...cardBase,
		...(lift
			? {
					border: `1px solid ${COLORS.borderStrong}`,
					boxShadow: "0 12px 30px rgba(28, 25, 23, 0.10)",
					transform: "translateY(-3px)",
			  }
			: {}),
		...(disabled ? { opacity: 0.4, cursor: "not-allowed" } : {}),
	}
	const inner = (
		<>
			<div style={iconTile(lift)}>
				<FontAwesomeIcon icon={icon} />
			</div>
			<span style={styles.cardLabel}>{label}</span>
		</>
	)
	const handlers = {
		onMouseEnter: () => setHover(true),
		onMouseLeave: () => setHover(false),
	}
	const card = to ? (
		<Link
			to={to}
			style={style}
			onClick={disabled ? (e) => e.preventDefault() : undefined}
		>
			{inner}
		</Link>
	) : (
		<div style={style} onClick={disabled ? undefined : onClick}>
			{inner}
		</div>
	)
	return (
		<div style={cardWrap} {...handlers}>
			{card}
		</div>
	)
}

// Onglets du dashboard dérivés de la config partagée (cf. navConfig).
const TABS = DASHBOARD_LAYOUT.map(({ label, value }) => ({ label, value }))

// Résout un item de config → props d'ActionCard. Les comportements dynamiques
// (fiche ouverte, client de passage, modal master, `from`) vivent ici, la
// structure dans navConfig. « Rechercher » n'est plus jamais désactivé : une fiche ouverte
// ailleurs ne bloque plus l'accès à la recherche.
const cardProps = (item, ctx) => {
	switch (item.kind) {
		case "fiche": {
			// Même règle que la top-bar (cf. navItemProps de NavV2) : la card
			// ramène à la DERNIÈRE FICHE VISITÉE de la session, grisée sinon.
			const f = ctx.fiche[item.domain]
			return {
				icon: item.icon,
				label: item.label,
				to: f.open ? `${f.route}/${f.id}` : "#",
				disabled: !f.open,
			}
		}
		case "passage":
			return {
				icon: item.icon,
				label: item.label,
				to: { pathname: "/atelier/fiche/0", from: "client-passage" },
			}
		case "master":
			return {
				icon: item.icon,
				label: `Fiche ${ctx.masterLabel}`,
				onClick: ctx.openMaster,
			}
		default:
			return {
				icon: item.icon,
				label: item.label,
				to: item.from
					? { pathname: item.to, from: item.from }
					: { pathname: item.to },
			}
	}
}

const Home = () => {
	const [factor, setFactor] = useState(0)
	const [showModalNewFicheAtelierMaster, setShowModalNewFicheAtelierMaster] =
		useState(false)

	const { masterEntite } = useSelector((state) => state.masterReducer)
	const { atelier } = useSelector((state) => state.atelierInfo)
	const { location } = useSelector((state) => state.locationInfo)
	const { dep } = useSelector((state) => state.depInfo)
	const { hasRight } = useRights()

	useEffect(() => {
		const toggleFactor = (e) => {
			if (e.key === "Escape") {
				setFactor((prev) => (prev > -2 ? prev - 1 : 0))
			}
		}
		const timer = setTimeout(
			() => window.addEventListener("keyup", toggleFactor),
			500,
		)
		return () => {
			clearTimeout(timer)
			window.removeEventListener("keyup", toggleFactor)
		}
	}, [])

	// Cards dérivées de la config partagée (même source que la top-bar). Le
	// domaine actif (factor) fournit l'ordre/sous-ensemble propre au dashboard ;
	// on filtre par droit et on résout les comportements dynamiques.
	// « Fiche » = dernière fiche visitée de la session, même prédicat que NavV2
	// (deux sentinelles cohabitent : dépannage pose 0, atelier/location null).
	const ficheMemorisee = (id) => id !== null && id !== undefined && id > 0
	const ctx = {
		fiche: {
			dep: {
				open: ficheMemorisee(dep.id),
				id: dep.id,
				route: "/depannage/fiche",
			},
			ate: {
				open: ficheMemorisee(atelier.id),
				id: atelier.id,
				route: "/atelier/fiche",
			},
			loc: {
				open: ficheMemorisee(location.id),
				id: location.id,
				route: "/location/fiche",
			},
		},
		masterLabel: masterEntite.label || "SVP",
		openMaster: () => setShowModalNewFicheAtelierMaster(true),
	}
	const activeLayout = DASHBOARD_LAYOUT.find((d) => d.value === factor)
	const activeCards = (activeLayout ? activeLayout.items : [])
		.map(resolveLayoutItem)
		.filter((it) => !it.right || hasRight(it.right))
		.map((it) => cardProps(it, ctx))

	return (
		<>
			{showModalNewFicheAtelierMaster && (
				<AtelierModalCreeFicheMaster
					visible={showModalNewFicheAtelierMaster}
					onClose={() => setShowModalNewFicheAtelierMaster(false)}
				/>
			)}
			<div style={styles.page}>
				<div style={styles.inner}>
					<h2 style={styles.title}>Menu</h2>
					<div style={styles.subtitle}>
						Accès rapide aux opérations
					</div>

					<div style={{ ...gs.segmented, marginBottom: 32 }}>
						{TABS.map((tab) => (
							<button
								key={tab.value}
								type="button"
								style={gs.segment(factor === tab.value)}
								onClick={() => setFactor(tab.value)}
							>
								{tab.label}
							</button>
						))}
					</div>

					<div style={styles.grid}>
						{activeCards.map((card) => (
							<ActionCard
								key={`${factor}-${card.label}`}
								icon={card.icon}
								label={card.label}
								to={card.to}
								onClick={card.onClick}
								disabled={card.disabled}
							/>
						))}
					</div>
				</div>
			</div>
		</>
	)
}

export default Home
